/**
 * /app/compras/ordenes — Supply Chain: gestión de órdenes de compra.
 * Listado + creación de OC a proveedores + envío por email / WhatsApp.
 * Gateado por supply_chain.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Mail, MessageCircle, Plus, X, Receipt } from "lucide-react";
import { DataTable, CrudEmptyState, Loading, Panel, QuickActions } from "@/components/ds";
import type { DataTableColumn, QuickAction } from "@/components/ds";
import { IngredientPickerDialog } from "@/components/IngredientPickerDialog";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useWorkspaceLocationId } from "@/lib/location-context";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import { toast } from "sonner";

export const Route = createFileRoute("/app/compras/ordenes")({
  component: ComprasOrdenesPage,
  validateSearch: (s: Record<string, unknown>) => ({
    ingredientId: typeof s.ingredientId === "string" && s.ingredientId ? s.ingredientId : undefined,
  }),
  head: () => ({ meta: [{ title: "Órdenes de compra — Supply Chain" }] }),
});

interface PurchaseOrder {
  id: string;
  numero: string | null;
  estado: string;
  supplierId: string | null;
  supplierName?: string;
  supplierEmail?: string | null;
  supplierPhone?: string | null;
  fechaEmision: string | null;
  fechaEntrega: string | null;
  total: number | null;
}
interface Supplier {
  id: string;
  nombre: string;
  email?: string | null;
  phone?: string | null;
}
interface Ingredient {
  id: string;
  nombre: string;
  unidadBase: string;
}
interface Location {
  id: string;
  name: string;
  active: boolean;
}

const STATUS_LABEL: Record<string, string> = {
  draft: "Borrador",
  sent: "Enviada",
  confirmed: "Confirmada",
  received: "Recibida",
  cancelled: "Cancelada",
};
const STATUS_COLOR: Record<string, string> = {
  draft: "text-muted-foreground",
  sent: "text-blue-400",
  confirmed: "text-amber-400",
  received: "text-green-400",
  cancelled: "text-red-400",
};

const pesos = (n: number) =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(n);

/** SCRUM-90: resumen de egresos ya vinculados a esta OC — trazabilidad bidireccional. */
interface LinkedExpenseSummary {
  count: number;
  total: number;
}

function ComprasOrdenesPage() {
  const navigate = useNavigate();
  const tid = useWorkspaceTenantId();
  // SCRUM-72: default desde el contexto único de sucursal activa — no un
  // selector aislado que arranca vacío en cada pantalla.
  const contextLocationId = useWorkspaceLocationId();
  const { ingredientId: prefillIngredientId } = Route.useSearch();
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [lock, setLock] = useState<PlanApiErrorPayload | null>(null);
  const [showForm, setShowForm] = useState(!!prefillIngredientId);
  const [saving, setSaving] = useState(false);
  const [sharingId, setSharingId] = useState<string | null>(null);
  const [lastCreated, setLastCreated] = useState<{ poId: string; numero: string } | null>(null);
  const [linkedExpenses, setLinkedExpenses] = useState<Record<string, LinkedExpenseSummary>>({});

  const [form, setForm] = useState({
    supplierId: "",
    fechaEntrega: "",
    notas: "",
    locationId: "",
  });
  const [lines, setLines] = useState<Array<{ ingredientId: string; qty: string; costoUnitario: string }>>(
    prefillIngredientId
      ? [{ ingredientId: prefillIngredientId, qty: "1", costoUnitario: "" }]
      : [],
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [previouslyPurchasedIds, setPreviouslyPurchasedIds] = useState<string[]>([]);

  const selectedSupplier = suppliers.find((s) => s.id === form.supplierId);

  useEffect(() => {
    if (!tid || !form.supplierId) {
      setPreviouslyPurchasedIds([]);
      return;
    }
    apiGetJson<{ ingredientIds?: string[] }>(
      `/api/supply-chain?resource=purchased-ingredients&supplierId=${form.supplierId}`,
      tid,
    )
      .then((r) => setPreviouslyPurchasedIds(r.ingredientIds ?? []))
      .catch(() => setPreviouslyPurchasedIds([]));
  }, [tid, form.supplierId]);

  const load = async () => {
    if (!tid) return;
    setLoading(true);
    try {
      const [or, sr, ir, lr] = await Promise.all([
        apiGetJson<{ orders?: PurchaseOrder[]; _lock?: PlanApiErrorPayload }>(
          `/api/supply-chain?resource=purchase-orders`,
          tid,
        ),
        apiGetJson<{ suppliers?: Supplier[] }>(`/api/supply-chain?resource=suppliers`, tid),
        apiGetJson<{ ingredients?: Ingredient[] }>(`/api/ingredients`, tid),
        apiGetJson<Location[]>(`/api/locations`, tid).catch(() => []),
      ]);
      if (or._lock) {
        setLock(or._lock);
        return;
      }
      setOrders(or.orders ?? []);
      setSuppliers(sr.suppliers ?? []);
      setIngredients(ir.ingredients ?? []);
      setLocations(lr.filter((l) => l.active));
    } catch {
      toast.error("Error al cargar órdenes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [tid]);

  // SCRUM-90: trazabilidad bidireccional — un vistazo de qué OCs ya tienen
  // un egreso registrado en Movimientos, sin exponer el resto de la caja.
  useEffect(() => {
    if (!tid || orders.length === 0) return;
    let cancelled = false;
    Promise.all(
      orders.map((o) =>
        apiGetJson<{ movements?: Array<{ amount: number }> }>(
          `/api/pos/movements/by-reference?referenceType=purchase_order&referenceId=${o.id}`,
          tid,
        )
          .then((r) => [o.id, r.movements ?? []] as const)
          .catch(() => [o.id, []] as const),
      ),
    ).then((entries) => {
      if (cancelled) return;
      const next: Record<string, LinkedExpenseSummary> = {};
      for (const [id, movements] of entries) {
        if (movements.length > 0) {
          next[id] = { count: movements.length, total: movements.reduce((s, m) => s + m.amount, 0) };
        }
      }
      setLinkedExpenses(next);
    });
    return () => {
      cancelled = true;
    };
  }, [tid, orders]);

  // SCRUM-72: si hay una sucursal activa en el contexto global y el campo
  // todavía no fue tocado, la usamos como default — sin pisar una elección
  // manual ya hecha en este formulario.
  useEffect(() => {
    if (contextLocationId && !form.locationId) {
      setForm((f) => ({ ...f, locationId: contextLocationId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextLocationId]);

  useEffect(() => {
    if (!prefillIngredientId) return;
    setShowForm(true);
    setLines((prev) =>
      prev.some((l) => l.ingredientId === prefillIngredientId)
        ? prev
        : [{ ingredientId: prefillIngredientId, qty: "1", costoUnitario: "" }, ...prev],
    );
  }, [prefillIngredientId]);

  const addIngredients = (ids: string[]) =>
    setLines((l) => [...l, ...ids.map((ingredientId) => ({ ingredientId, qty: "1", costoUnitario: "" }))]);
  const removeLine = (i: number) => setLines((l) => l.filter((_, idx) => idx !== i));
  const updateLine = (i: number, field: string, value: string) => {
    setLines((l) => l.map((line, idx) => (idx === i ? { ...line, [field]: value } : line)));
  };

  const totalEstimado = lines.reduce((s, l) => {
    const qty = parseFloat(l.qty) || 0;
    const costo = parseFloat(l.costoUnitario) || 0;
    return s + qty * costo;
  }, 0);

  const shareWhatsApp = async (poId: string) => {
    if (!tid) return;
    setSharingId(poId);
    try {
      const detail = await apiGetJson<{
        whatsappUrl?: string | null;
        canWhatsApp?: boolean;
        error?: string;
      }>(`/api/supply-chain?resource=purchase-order-detail&id=${encodeURIComponent(poId)}`, tid);
      if (!detail.whatsappUrl) {
        toast.error("El proveedor no tiene teléfono / WhatsApp cargado");
        return;
      }
      window.open(detail.whatsappUrl, "_blank", "noopener,noreferrer");
      await apiSendJson(`/api/supply-chain`, "POST", { action: "mark-po-sent", poId }, tid);
      toast.success("WhatsApp abierto — marcamos la OC como enviada");
      setLastCreated(null);
      await load();
    } catch {
      toast.error("No se pudo preparar el mensaje de WhatsApp");
    } finally {
      setSharingId(null);
    }
  };

  const shareEmail = async (poId: string) => {
    if (!tid) return;
    setSharingId(poId);
    try {
      await apiSendJson(`/api/supply-chain`, "POST", { action: "send-po-email", poId }, tid);
      toast.success("Orden de compra enviada por email");
      setLastCreated(null);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo enviar el email");
    } finally {
      setSharingId(null);
    }
  };

  const handleCrear = async () => {
    if (!tid || lines.length === 0) {
      toast.error("Agregá al menos un ítem");
      return;
    }
    if (!form.supplierId) {
      toast.error("Seleccioná un proveedor");
      return;
    }
    setSaving(true);
    try {
      const res = await apiSendJson<{ ok: boolean; poId: string; numero: string }>(
        `/api/supply-chain`,
        "POST",
        {
          action: "create-po",
          supplierId: form.supplierId,
          fechaEntrega: form.fechaEntrega || null,
          notas: form.notas || null,
          locationId: form.locationId || null,
          lines: lines.map((l) => ({
            ingredientId: l.ingredientId,
            qty: parseFloat(l.qty),
            costoUnitario: parseFloat(l.costoUnitario) || null,
          })),
        },
        tid,
      );
      toast.success(`Orden ${res.numero} creada`);
      setLastCreated({ poId: res.poId, numero: res.numero });
      setShowForm(false);
      setForm({ supplierId: "", fechaEntrega: "", notas: "", locationId: "" });
      setLines([]);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al crear la OC");
    } finally {
      setSaving(false);
    }
  };

  const columns: DataTableColumn<PurchaseOrder>[] = [
    { key: "numero", header: "Número", render: (o) => <span className="font-medium tabular-nums">{o.numero ?? o.id.slice(-8)}</span> },
    {
      key: "estado",
      header: "Estado",
      render: (o) => (
        <span className={`text-xs font-medium ${STATUS_COLOR[o.estado] ?? "text-muted-foreground"}`}>
          {STATUS_LABEL[o.estado] ?? o.estado}
        </span>
      ),
    },
    { key: "proveedor", header: "Proveedor", render: (o) => <span className="text-muted-foreground">{o.supplierName ?? "—"}</span> },
    {
      key: "emision",
      header: "Emisión",
      render: (o) => <span className="text-xs text-muted-foreground">{o.fechaEmision ? new Date(o.fechaEmision).toLocaleDateString("es-AR") : "—"}</span>,
    },
    {
      key: "entrega",
      header: "Entrega",
      render: (o) => <span className="text-xs text-muted-foreground">{o.fechaEntrega ? new Date(o.fechaEntrega).toLocaleDateString("es-AR") : "—"}</span>,
    },
    {
      key: "total",
      header: "Total",
      className: "text-right",
      render: (o) => <span className="tabular-nums font-medium">{o.total != null ? pesos(o.total) : "—"}</span>,
    },
    {
      key: "egresos",
      header: "Egresos",
      render: (o) => {
        const summary = linkedExpenses[o.id];
        return summary ? (
          <Badge variant="outline" className="text-[10px]">
            {summary.count} · {pesos(summary.total)}
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        );
      },
    },
    {
      key: "enviar",
      header: "Enviar",
      className: "text-right",
      render: (o) => (
        <div className="flex justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            title="Registrar el egreso de esta orden en Movimientos de caja"
            onClick={() =>
              navigate({
                to: "/app/pos/movimientos",
                search: { linkKind: "purchase_order", linkId: o.id, linkAmount: o.total ?? undefined },
              })
            }
          >
            <Receipt className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            title={o.supplierEmail ? `Email a ${o.supplierEmail}` : "Sin email del proveedor"}
            disabled={!o.supplierEmail || sharingId === o.id}
            onClick={() => shareEmail(o.id)}
          >
            <Mail className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            title={o.supplierPhone ? `WhatsApp a ${o.supplierPhone}` : "Sin teléfono del proveedor"}
            disabled={!o.supplierPhone || sharingId === o.id}
            onClick={() => shareWhatsApp(o.id)}
          >
            <MessageCircle className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ];

  const headerActions: QuickAction[] = [
    { key: "nueva-oc", label: "Nueva OC", icon: Plus, onClick: () => setShowForm((v) => !v) },
  ];

  if (loading) return <Loading variant="list" rows={3} />;
  if (lock) return <FeatureLockedState payload={lock} />;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <QuickActions actions={headerActions} />
      </div>

      {lastCreated && (
        <Panel
          title={`OC ${lastCreated.numero} lista para enviar`}
          actions={
            <button type="button" onClick={() => setLastCreated(null)} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          }
        >
          <p className="text-sm text-muted-foreground mb-3">
            Enviá la orden al proveedor por email o abrí WhatsApp con el mensaje formateado.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => shareEmail(lastCreated.poId)}
              disabled={sharingId === lastCreated.poId}
              className="gap-2"
            >
              <Mail className="h-4 w-4" /> Enviar por email
            </Button>
            <Button
              variant="secondary"
              onClick={() => shareWhatsApp(lastCreated.poId)}
              disabled={sharingId === lastCreated.poId}
              className="gap-2"
            >
              <MessageCircle className="h-4 w-4" /> Abrir WhatsApp
            </Button>
          </div>
        </Panel>
      )}

      {showForm && (
        <Panel
          title="Nueva orden de compra"
          actions={
            <button
              onClick={() => setShowForm(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          }
        >
          <div className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-48">
              <label className="text-xs text-muted-foreground">Proveedor *</label>
              <select
                value={form.supplierId}
                onChange={(e) => setForm((f) => ({ ...f, supplierId: e.target.value }))}
                className="mt-1 block w-full rounded-xl border border-border/60 bg-card px-3 py-2 text-sm"
              >
                <option value="">Seleccioná un proveedor</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nombre}
                    {!s.email && !s.phone ? " (sin contacto)" : ""}
                  </option>
                ))}
              </select>
              {selectedSupplier && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {[selectedSupplier.email, selectedSupplier.phone].filter(Boolean).join(" · ") ||
                    "Cargá email/teléfono del proveedor para poder enviar la OC"}
                </p>
              )}
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Fecha de entrega esperada</label>
              <input
                type="date"
                value={form.fechaEntrega}
                onChange={(e) => setForm((f) => ({ ...f, fechaEntrega: e.target.value }))}
                className="mt-1 block rounded-xl border border-border/60 bg-card px-3 py-2 text-sm"
              />
            </div>
            {locations.length >= 2 && (
              <div className="flex-1 min-w-48">
                <label className="text-xs text-muted-foreground">Sucursal que recibe</label>
                <select
                  value={form.locationId}
                  onChange={(e) => setForm((f) => ({ ...f, locationId: e.target.value }))}
                  className="mt-1 block w-full rounded-xl border border-border/60 bg-card px-3 py-2 text-sm"
                >
                  <option value="">Default del comercio</option>
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Notas para el proveedor</label>
            <input
              value={form.notas}
              onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))}
              placeholder="Horario de recepción, referencia, etc."
              className="mt-1 block w-full rounded-xl border border-border/60 bg-card px-3 py-2 text-sm"
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Ítems</p>
              <Button size="sm" variant="ghost" onClick={() => setPickerOpen(true)}>
                <Plus className="h-3.5 w-3.5 mr-1" /> Agregar ítem
              </Button>
            </div>
            {lines.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">Sin ítems — hacé click en "Agregar ítem"</p>
            ) : (
              <div className="space-y-2">
                {lines.map((line, i) => (
                  <div
                    key={i}
                    className="flex flex-wrap items-center gap-2 rounded-xl border border-border/40 bg-card p-2"
                  >
                    <span className="flex-1 min-w-40 truncate text-sm px-1">
                      {ingredients.find((ing) => ing.id === line.ingredientId)?.nombre ?? "Ingrediente eliminado"}
                    </span>
                    <input
                      type="number"
                      min="0.001"
                      step="0.001"
                      value={line.qty}
                      onChange={(e) => updateLine(i, "qty", e.target.value)}
                      placeholder="Cantidad"
                      className="w-24 rounded-lg border border-border/60 bg-card px-2 py-1.5 text-sm text-right tabular-nums"
                    />
                    <input
                      type="number"
                      min="0"
                      value={line.costoUnitario}
                      onChange={(e) => updateLine(i, "costoUnitario", e.target.value)}
                      placeholder="Costo unit."
                      className="w-28 rounded-lg border border-border/60 bg-card px-2 py-1.5 text-sm text-right tabular-nums"
                    />
                    <span className="text-xs tabular-nums text-muted-foreground min-w-[80px] text-right">
                      {pesos((parseFloat(line.qty) || 0) * (parseFloat(line.costoUnitario) || 0))}
                    </span>
                    <button
                      onClick={() => removeLine(i)}
                      className="text-muted-foreground hover:text-red-400"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <div className="flex justify-end pt-1">
                  <p className="text-sm font-bold tabular-nums">
                    Total estimado: <span className="text-primary">{pesos(totalEstimado)}</span>
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <Button onClick={handleCrear} disabled={saving || lines.length === 0 || !form.supplierId}>
              {saving ? "Creando…" : "Crear OC"}
            </Button>
            <Button variant="ghost" onClick={() => setShowForm(false)}>
              Cancelar
            </Button>
          </div>
          </div>
        </Panel>
      )}

      <DataTable
        columns={columns}
        rows={orders}
        getRowKey={(o) => o.id}
        emptyState={<CrudEmptyState title="Sin órdenes de compra" />}
      />

      <IngredientPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        options={ingredients}
        previouslyPurchasedIds={previouslyPurchasedIds}
        alreadyAddedIds={lines.map((l) => l.ingredientId)}
        onConfirm={addIngredients}
      />
    </div>
  );
}
